import http from './http';
import type { StreamingEventPayload } from './messages';

export type RouteNamespace =
  | 'pif-report'
  | 'pif-report-sheet'
  | 'deutsche-bank-report'
  | 'deutsche-bank-report-sheet';

export type StreamMessagesOptions = {
  accountId: string | number;
  chatId: string | number | null;
  routeNamespace: RouteNamespace;
  sheetId?: number | null;
  payload: {
    content: string;
    category_ids?: number[];
    chat_id?: string | number | null;
    mode?: string;
    workflow?: string;
  };
  files?: File[];
  token?: string;
  signal?: AbortSignal;
};

const buildStreamingUrl = (
  accountId: string | number,
  chatId: string | number | null,
  routeNamespace: RouteNamespace,
  sheetId?: number | null
): string => {
  const isDeutscheBankRoute = routeNamespace.startsWith('deutsche-bank');
  const isSheetRoute = routeNamespace.endsWith('-sheet');
  const base = `v1/${isDeutscheBankRoute ? 'deutsche-bank' : 'pif'}/accounts/${accountId}`;
  const reportSegment =
    isSheetRoute && sheetId != null
      ? `/report/sheets/${sheetId}`
      : '/report';

  const endpoint =
    chatId == null
      ? `${base}${reportSegment}/chats/messages/streaming`
      : `${base}${reportSegment}/chats/${chatId}/messages/streaming`;
  const baseUrl = http.defaults?.baseURL ?? undefined;
  const normalizedBase = baseUrl ? baseUrl.replace(/\/+$/, '') : '';
  const normalizedEndpoint = endpoint.replace(/^\/+/, '');
  return normalizedBase ? `${normalizedBase}/${normalizedEndpoint}` : `/${normalizedEndpoint}`;
};

const isReportRoute = (routeNamespace: RouteNamespace) =>
  routeNamespace === 'pif-report' || routeNamespace === 'deutsche-bank-report';

const decoder = new TextDecoder();

const emitPayload = (chunk: string): StreamingEventPayload | null => {
  const trimmed = chunk.trim();
  if (!trimmed) return null;
  const payload = trimmed.startsWith('data:') ? trimmed.slice(5).trim() : trimmed;
  if (!payload) return null;
  try {
    return JSON.parse(payload) as StreamingEventPayload;
  } catch (error) {
    if (import.meta.env?.DEV) {
      console.debug('[streamMessages] Failed to parse event payload', error, payload);
    }
    return null;
  }
};

export async function* streamMessages({
  accountId,
  chatId,
  routeNamespace,
  sheetId,
  payload,
  files,
  token,
  signal,
}: StreamMessagesOptions): AsyncGenerator<StreamingEventPayload, void, void> {
  const targetUrl = buildStreamingUrl(accountId, chatId, routeNamespace, sheetId);
  const controller = new AbortController();
  if (signal) {
    if (signal.aborted) {
      controller.abort(signal.reason);
    } else {
      const listener = () => controller.abort(signal.reason);
      signal.addEventListener('abort', listener, { once: true });
      controller.signal.addEventListener(
        'abort',
        () => {
          signal.removeEventListener('abort', listener);
        },
        { once: true }
      );
    }
  }

  const hasFiles = files && files.length > 0;
  const mode = payload.mode ?? 'SAFE_MODE';

  let fetchBody: BodyInit;
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (hasFiles) {
    const formData = new FormData();
    formData.append('content', payload.content);
    formData.append('chat_id', String(payload.chat_id ?? chatId ?? ''));
    formData.append('mode', mode);
    if (payload.workflow) {
      formData.append('workflow', payload.workflow);
    }
    if (isReportRoute(routeNamespace) && payload.category_ids?.length) {
      formData.append('category_ids', JSON.stringify(payload.category_ids));
    }
    for (const file of files) {
      formData.append('files', file, file.name);
    }
    fetchBody = formData;
  } else {
    headers['Content-Type'] = 'application/json';
    const body: Record<string, unknown> = {
      content: payload.content,
      chat_id: payload.chat_id ?? chatId ?? null,
      mode,
    };
    if (payload.workflow) {
      body.workflow = payload.workflow;
    }
    if (isReportRoute(routeNamespace) && payload.category_ids?.length) {
      body.category_ids = payload.category_ids;
    }
    fetchBody = JSON.stringify(body);
  }

  const response = await fetch(targetUrl, {
    method: 'POST',
    headers,
    credentials: 'include',
    cache: 'no-cache',
    body: fetchBody,
    signal: controller.signal,
  });

  if (!response.ok) {
    throw new Error(`Streaming request failed with status ${response.status}`);
  }
  if (!response.body) return;

  const reader = response.body.getReader();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      const chunk = value ? decoder.decode(value, { stream: !done }) : '';
      buffer += chunk;
      const parts = buffer.split(/\n\n|\r\n\r\n|\r\n|\n/);
      buffer = parts.pop() ?? '';
      for (const part of parts) {
        const event = emitPayload(part);
        if (event) {
          yield event;
        }
      }
      if (done) break;
    }
    buffer += decoder.decode();
    if (buffer.trim()) {
      const event = emitPayload(buffer);
      if (event) {
        yield event;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch (error) {
      if (import.meta.env?.DEV) {
        console.debug('[streamMessages] Failed to release reader lock', error);
      }
    }
    controller.abort();
  }
}
